import React, { useRef } from 'react';
import { ChevronUp, ChevronDown, CalendarCheck } from 'lucide-react';
import { bucketByPhase, groupByMonth, colorForMonth, PHASES, getSGToday } from '@/utils/dateGrouping';

/**
 * Render a flat list of items grouped by phase (Upcoming / Last 3 Days /
 * Expired) and then by month inside each phase.
 *
 * UX layer:
 *   • A right-side floating dock (vertically centered, fixed) bundles
 *     [↑ scroll top] · 3 phase pills · [↓ scroll bottom]. Each pill shows
 *     a colored dot + count and smooth-scrolls to that section. Pills
 *     auto-grey when their phase has 0 items. Hover reveals the label.
 *
 * Sort order applied per phase:
 *   • Upcoming  → ASC chronological (current month → Dec → Jan next year → …).
 *   • Last 3 Days → DESC (most recent first).
 *   • Expired   → DESC (most recent first).
 */
export const DateBucketedList = ({
  items,
  getDate,
  renderItem,
  emptyLabel = 'No records.',
  testidPrefix = 'date-bucketed-list',
  showEmptyPhases = false,
}) => {
  const buckets = bucketByPhase(items, getDate);
  const total = buckets.upcoming.length + buckets.last3.length + buckets.expired.length;

  const rootRef = useRef(null);
  const refs = {
    upcoming: useRef(null),
    last3: useRef(null),
    expired: useRef(null),
  };

  // Find the nearest scrollable ancestor (the dashboard's inner div is a
  // scroll container, not window). Falls back to documentElement.
  const getScrollContainer = () => {
    let el = rootRef.current?.parentElement;
    while (el) {
      const cs = getComputedStyle(el);
      if (/(auto|scroll)/.test(cs.overflowY) && el.scrollHeight > el.clientHeight) return el;
      el = el.parentElement;
    }
    return document.scrollingElement || document.documentElement;
  };

  const handleJump = (key) => {
    const node = refs[key]?.current;
    const container = getScrollContainer();
    if (!node || !container) return;
    // Manually scroll only the dashboard's inner container (MAIN). Native
    // `scrollIntoView` on mobile also scrolls the document body, which
    // shifts the dashboard header and disorients the user.
    const cRect = container.getBoundingClientRect();
    const nRect = node.getBoundingClientRect();
    const offset = nRect.top - cRect.top + container.scrollTop - 12;
    container.scrollTo({ top: Math.max(0, offset), behavior: 'smooth' });
  };
  const scrollTop = () => {
    const c = getScrollContainer();
    c.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const scrollBottom = () => {
    const c = getScrollContainer();
    c.scrollTo({ top: c.scrollHeight, behavior: 'smooth' });
  };

  // "Today" jump — find the first card matching today's SG-local date, scroll
  // it to the centre of the inner container, and pulse it briefly.
  const jumpToToday = () => {
    const today = getSGToday();
    const container = getScrollContainer();
    if (!container) return;
    const cards = rootRef.current?.querySelectorAll('[data-testid$="-card"], [class*="dashboard-card"]') || [];
    for (const el of cards) {
      if (el.textContent && el.textContent.includes(today)) {
        const cRect = container.getBoundingClientRect();
        const eRect = el.getBoundingClientRect();
        const offset = eRect.top - cRect.top + container.scrollTop - (container.clientHeight / 2 - eRect.height / 2);
        container.scrollTo({ top: Math.max(0, offset), behavior: 'smooth' });
        el.animate(
          [
            { boxShadow: '0 0 0 0 rgba(16,185,129,0)' },
            { boxShadow: '0 0 0 6px rgba(16,185,129,0.35)' },
            { boxShadow: '0 0 0 0 rgba(16,185,129,0)' },
          ],
          { duration: 1600, iterations: 1 }
        );
        return;
      }
    }
    handleJump('upcoming');
  };
  const hasToday = items.some((it) => getDate(it) === getSGToday());

  if (total === 0) {
    return <p ref={rootRef} className="text-sm text-slate-500 py-4" data-testid={`${testidPrefix}-empty`}>{emptyLabel}</p>;
  }

  const dockBtn =
    'group inline-flex items-center justify-center h-9 w-9 rounded-full border shadow-sm transition-all hover:shadow-md hover:scale-105';

  return (
    <div ref={rootRef} className="relative">
      {/* Floating right-side dock — always visible on md+ */}
      <aside
        className="fixed right-4 top-1/2 -translate-y-1/2 z-30 hidden md:flex flex-col items-end gap-2"
        data-testid={`${testidPrefix}-floating-dock`}
        aria-label="List navigation"
      >
        <button
          type="button"
          onClick={scrollTop}
          className={`${dockBtn} bg-slate-900 border-slate-900 text-white hover:bg-slate-700 hover:border-slate-700`}
          title="Scroll to top"
          data-testid={`${testidPrefix}-scroll-top`}
        >
          <ChevronUp className="h-4 w-4" />
        </button>

        {/* Today pill — only enabled when an item exists for today's date. */}
        <button
          type="button"
          onClick={jumpToToday}
          disabled={!hasToday}
          title={hasToday ? 'Jump to today' : 'No items for today'}
          data-testid={`${testidPrefix}-today`}
          className={`group inline-flex items-center gap-2 pr-3 pl-2 h-9 rounded-full border bg-white shadow-sm transition-all ${
            hasToday
              ? 'border-emerald-300 text-emerald-700 hover:border-emerald-600 hover:bg-emerald-600 hover:text-white hover:shadow-md'
              : 'border-slate-200 text-slate-400 cursor-not-allowed bg-slate-50'
          }`}
        >
          <CalendarCheck className="h-3.5 w-3.5" />
          <span className="hidden lg:group-hover:inline text-xs font-semibold whitespace-nowrap">Today</span>
        </button>

        {PHASES.map((phase) => {
          const count = buckets[phase.key].length;
          const disabled = count === 0;
          return (
            <button
              key={phase.key}
              type="button"
              onClick={() => !disabled && handleJump(phase.key)}
              disabled={disabled}
              title={`${phase.label} · ${count}`}
              data-testid={`${testidPrefix}-shortcut-${phase.key}`}
              className={`group inline-flex items-center gap-2 pr-3 pl-2 h-9 rounded-full border bg-white shadow-sm transition-all hover:shadow-md ${
                disabled
                  ? 'border-slate-200 text-slate-400 cursor-not-allowed bg-slate-50'
                  : 'border-slate-200 text-slate-700 hover:border-slate-900 hover:bg-slate-900 hover:text-white'
              }`}
            >
              <span className={`inline-block h-2 w-2 rounded-full ${phase.dot}`} />
              {/* Label hidden on small docks; revealed on hover for a clean compact look. */}
              <span className="hidden lg:group-hover:inline text-xs font-semibold whitespace-nowrap">
                {phase.label}
              </span>
              <span
                className={`min-w-[1.25rem] text-center text-[10px] font-bold rounded-full px-1.5 py-0.5 ${
                  disabled ? 'bg-slate-100 text-slate-400' : 'bg-slate-100 text-slate-700 group-hover:bg-white group-hover:text-slate-900'
                }`}
              >
                {count}
              </span>
            </button>
          );
        })}

        <button
          type="button"
          onClick={scrollBottom}
          className={`${dockBtn} bg-slate-900 border-slate-900 text-white hover:bg-slate-700 hover:border-slate-700`}
          title="Scroll to bottom"
          data-testid={`${testidPrefix}-scroll-bottom`}
        >
          <ChevronDown className="h-4 w-4" />
        </button>
      </aside>

      {/* Mobile: floating bottom dock — two rows.
          Row 1: ▲ scroll-top + Today + ▼ scroll-bottom (always visible, centered).
          Row 2: phase pills (Upcoming / Last 3 Days / Expired) — horizontally
          scrollable if the row exceeds the screen width on tiny phones, but
          the arrows + Today on row 1 remain always reachable.
          `bottom: max(0.75rem, env(safe-area-inset-bottom))` keeps the dock
          above the iOS home indicator. */}
      <div
        className="md:hidden fixed left-2 right-2 z-30 flex flex-col gap-1.5 px-2 py-2 rounded-2xl bg-white/95 backdrop-blur-md shadow-xl border border-slate-200"
        style={{ bottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
        data-testid={`${testidPrefix}-shortcuts-mobile`}
      >
        {/* Row 1 — arrows always visible + Today (always-on access) */}
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={scrollTop}
            className="inline-flex items-center justify-center h-8 w-8 rounded-full border border-slate-900 bg-slate-900 text-white shadow-sm"
            title="Scroll to top"
            data-testid={`${testidPrefix}-scroll-top-mobile`}
          >
            <ChevronUp className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={jumpToToday}
            disabled={!hasToday}
            title={hasToday ? 'Jump to today' : 'No items for today'}
            data-testid={`${testidPrefix}-today-mobile`}
            className={`inline-flex items-center gap-1 px-3 py-1 rounded-full border text-[11px] font-semibold transition-colors ${
              hasToday
                ? 'border-emerald-300 text-emerald-700 bg-white'
                : 'border-slate-200 text-slate-400 cursor-not-allowed bg-slate-50'
            }`}
          >
            <CalendarCheck className="h-3 w-3" />
            <span>Today</span>
          </button>
          <button
            type="button"
            onClick={scrollBottom}
            className="inline-flex items-center justify-center h-8 w-8 rounded-full border border-slate-900 bg-slate-900 text-white shadow-sm"
            title="Scroll to bottom"
            data-testid={`${testidPrefix}-scroll-bottom-mobile`}
          >
            <ChevronDown className="h-4 w-4" />
          </button>
        </div>
        {/* Row 2 — phase pills (horizontally scrollable on very narrow screens) */}
        <div className="flex items-center justify-center gap-1.5 overflow-x-auto pb-0.5 -mx-1 px-1">
          {PHASES.map((phase) => {
            const count = buckets[phase.key].length;
            const disabled = count === 0;
            return (
              <button
                key={phase.key}
                type="button"
                onClick={() => !disabled && handleJump(phase.key)}
                disabled={disabled}
                data-testid={`${testidPrefix}-shortcut-mobile-${phase.key}`}
                className={`flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[11px] font-semibold transition-colors ${
                  disabled
                    ? 'border-slate-200 text-slate-400 cursor-not-allowed bg-slate-50'
                    : 'border-slate-200 text-slate-700 bg-white'
                }`}
              >
                <span className={`inline-block h-1.5 w-1.5 rounded-full ${phase.dot}`} />
                <span className="whitespace-nowrap">{phase.label}</span>
                <span className={`px-1 rounded-full text-[10px] font-bold ${disabled ? 'bg-slate-100 text-slate-400' : 'bg-slate-100 text-slate-700'}`}>{count}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Bottom padding spacer so the floating mobile dock doesn't cover the last card. */}
      <div className="md:hidden h-28" aria-hidden="true" />

      <div className="space-y-8">
        {PHASES.map((phase) => {
          const list = buckets[phase.key];
          if (list.length === 0 && !showEmptyPhases) return null;
          const monthOrder = phase.key === 'upcoming' ? 'asc' : 'desc';
          const months = groupByMonth(list, getDate, monthOrder);
          return (
            <section
              key={phase.key}
              ref={refs[phase.key]}
              data-testid={`${testidPrefix}-${phase.testid}`}
              style={{ scrollMarginTop: '5rem' }}
            >
              <div className="flex items-center gap-2 mb-3">
                <span className={`inline-block h-2 w-2 rounded-full ${phase.dot}`} />
                <h2 className={`text-xs font-bold uppercase tracking-[0.18em] ${phase.text}`}>{phase.label}</h2>
                <span className="px-2 py-0.5 text-[11px] font-semibold rounded-full bg-slate-100 text-slate-600">{list.length}</span>
              </div>
              {list.length === 0 ? (
                <p className="text-xs text-slate-400 italic">No items.</p>
              ) : (
                <div className="space-y-6">
                  {months.map(({ monthKey, monthLabel, items: monthItems }) => {
                    const c = colorForMonth(monthKey);
                    return (
                      <div key={monthKey} data-testid={`${testidPrefix}-month-${monthKey}`}>
                        <div className={`flex items-center gap-3 mb-3 px-3 py-1.5 rounded-md border-l-4 ${c.border} ${c.bg}`}>
                          <span className={`inline-block h-1.5 w-1.5 rounded-full ${c.dot}`} />
                          <h3 className={`text-sm font-semibold tracking-tight ${c.text}`}>{monthLabel}</h3>
                          <span className={`ml-auto px-2 py-0.5 text-[10px] font-semibold rounded-full bg-white/70 ${c.text}`}>{monthItems.length}</span>
                        </div>
                        <div className="space-y-3">
                          {monthItems.map(renderItem)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
};

export default DateBucketedList;
